<template>
  <!-- Messages d'erreur/succès -->
  <div v-if="errors !== ''" class="alert alert-danger alert-dismissible fade show m-3" role="alert">
    <h6 v-if="errorId !== ''" class="fw-bold mb-1">{{ errorId }}</h6>
    <p class="mb-0" style="white-space: pre-line;">{{ errors }}</p>
    <button type="button" class="btn-close" @click="errors = ''"></button>
  </div>
  <div v-if="success !== ''" class="alert alert-success alert-dismissible fade show m-3" role="alert">
    <p class="mb-0">{{ success }}</p>
    <button type="button" class="btn-close" @click="success = ''"></button>
  </div>

  <!-- Loader -->
  <div class="clearfix" v-if="!loader">
    <div class="spinner-grow text-danger" style="width: 3rem; height: 3rem; margin-top: 25vh; margin-left: 42%; margin-bottom: 5vh" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <div class="text-center" style="margin-top: -5vh;">
      <p class="text-muted">Chargement en cours...</p>
    </div>
  </div>

  <!-- Contenu principal -->
  <div v-if="loader" class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <!-- En-tête avec filtres -->
    <div class="card-head box-shadow bg-white p-15 p-sm-20 p-md-25">
      <div class="row g-3 align-items-center">
        <!-- Solde disponible -->
        <div class="col-12">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <div>
              <span class="text-muted fs-13 text-uppercase fw-semibold">Solde disponible</span>
              <h5 class="mb-0 fw-bold" :class="totalTransfertAmount > payoutBalance ? 'text-danger' : 'text-success'">
                {{ formatNumber(payoutBalance) }} XOF
              </h5>
            </div>
            <div v-if="transferts.length > 0" class="text-end">
              <span class="text-muted fs-13 text-uppercase fw-semibold">Montant à transférer</span>
              <h5 class="mb-0 fw-bold" :class="totalTransfertAmount > payoutBalance ? 'text-danger' : 'text-dark'">
                {{ formatNumber(totalTransfertAmount) }} XOF
              </h5>
            </div>
          </div>
          <div v-if="transferts.length > 0 && totalTransfertAmount > payoutBalance" class="alert alert-danger py-2 px-3 mb-2">
            <i class="flaticon-alert me-1"></i>
            Solde insuffisant pour effectuer ce transfert
          </div>
        </div>

        <!-- Filtres -->
        <div class="col-12 col-lg-8">
          <div class="row g-2">
            <div class="col-12 col-sm-6 col-lg-3">
              <select
                class="form-select form-select-sm shadow-none"
                @change="handleBusinessChange"
                v-model="selectedBusiness"
              >
                <option value="">Sélectionnez une catégorie</option>
                <option v-for="item in business" :key="item.code" :value="item.code">
                  {{ item.name }}
                </option>
              </select>
            </div>
            <div class="col-12 col-sm-6 col-lg-3" v-if="showSubBusiness">
              <select
                class="form-select form-select-sm shadow-none"
                @change="handleSubBusinessChange"
                v-model="selectedSubBusiness"
              >
                <option value="">Sélectionnez une sous-catégorie</option>
                <option v-for="item in subBusiness" :key="item.code" :value="item.code">
                  {{ item.name }}
                </option>
              </select>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <select
                class="form-select form-select-sm shadow-none"
                @change="handleCountryChange"
                v-model="selectedCountry"
              >
                <option value="">Sélectionnez le pays</option>
                <option value="SN">Sénégal (9 chiffres)</option>
                <option value="CI">Côte d'Ivoire (10 chiffres)</option>
              </select>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <input
                v-model="transferDate"
                type="date"
                class="form-control form-control-sm shadow-none"
                placeholder="Date de transfert"
              />
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="col-12 col-lg-4">
          <div class="d-flex flex-wrap gap-2 justify-content-lg-end">
            <!-- Upload de fichier -->
            <button
              class="btn btn-sm btn-outline-primary"
              @click="triggerFileUpload"
            >
              <i class="flaticon-upload me-1"></i>
              Importer fichier
            </button>

            <input
              type="file"
              id="fileUpload"
              ref="fileInput"
              accept=".xlsx,.xls,.csv"
              style="display: none"
              @change="handleFileUpload"
            />

            <button
              class="btn btn-sm btn-outline-secondary"
              @click="downloadTemplate"
            >
              <i class="flaticon-file-1 me-1"></i>
              Télécharger modèle
            </button>

            <button
              class="btn btn-sm text-white"
              style="background-color: #EB9355;"
              @click="openImportModal"
            >
              <i class="flaticon-plus me-1"></i>
              Importer contacts
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Corps du tableau -->
    <div class="card-body p-15 p-sm-20 p-md-25">
      <!-- Zone de drag & drop -->
      <div
        class="drop-zone p-4 text-center border-2 border-dashed rounded-3 mb-3"
        :class="{ 'border-primary bg-primary bg-opacity-10': isDragging }"
        @dragover.prevent="isDragging = true"
        @dragenter.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        v-if="transferts.length === 0"
      >
        <i class="flaticon-upload fs-1 text-muted d-block mb-2"></i>
        <p class="text-muted">
          {{ fileName || 'Glissez-déposez votre fichier Excel ici ou cliquez sur "Importer fichier"' }}
        </p>
        <p class="text-muted small">
          Formats acceptés: .xlsx, .xls, .csv
        </p>
        <span v-if="selectedCountry" class="badge bg-success">
          Pays: {{ selectedCountry }} ({{ getPhoneLength(selectedCountry) }} chiffres)
        </span>
        <span v-else class="badge bg-danger">
          Veuillez sélectionner un pays
        </span>
      </div>

      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th scope="col" class="fw-medium text-uppercase fs-13 ps-0">
                NOM & PRÉNOM(S)
              </th>
              <th scope="col" class="fw-medium text-uppercase fs-13">
                TÉLÉPHONE
              </th>
              <th scope="col" class="fw-medium text-uppercase fs-13 text-end">
                MONTANT
              </th>
              <th scope="col" class="fw-medium text-uppercase fs-13">
                OPÉRATEUR
              </th>
              <th scope="col" class="fw-medium text-uppercase fs-13 text-end">
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="transferts.length === 0">
              <td colspan="5" class="text-center text-muted py-30">
                <i class="flaticon-info fs-2 d-block mb-2"></i>
                Aucun bénéficiaire importé
              </td>
            </tr>
            <tr v-for="(transfert, index) in paginatedTransferts" :key="transfert.code || index">
              <td class="ps-0">
                <div class="d-flex align-items-center">
                  <input class="form-check-input me-2" type="checkbox" />
                  <span>{{ transfert.clientId }}</span>
                </div>
              </td>
              <td>
                <span :class="{ 'text-danger': transfert.errors && transfert.errors.length > 0 }">
                  {{ transfert.mobile }}
                </span>
                <span v-if="transfert.errors && transfert.errors.length > 0" class="badge bg-danger ms-1" title="Erreurs">
                  {{ transfert.errors.length }}
                </span>
              </td>
              <td class="text-end fw-semibold" :class="{ 'text-danger': transfert.errors && transfert.errors.length > 0 }">
                {{ formatNumber(transfert.amount) }}
              </td>
              <td>
                <span class="badge bg-light text-dark">{{ transfert.operator || 'Non défini' }}</span>
              </td>
              <td class="text-end">
                <button
                  class="btn btn-sm btn-outline-danger"
                  @click="removeBeneficiary(index)"
                >
                  <i class="flaticon-delete"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="transferts.length > 0" class="pagination-area d-md-flex mt-15 mt-md-25 mb-0 justify-content-between align-items-center">
        <div class="d-flex flex-wrap gap-3 align-items-center">
          <span class="text-paragraph">
            <i class="flaticon-users me-1"></i>
            Total bénéficiaires:
            <span class="fw-bold">{{ totalBeneficiaries }}</span>
          </span>
          <span class="text-paragraph">
            <i class="flaticon-money me-1"></i>
            Montant total:
            <span class="fw-bold" :class="totalTransfertAmount > payoutBalance ? 'text-danger' : 'text-success'">{{ formatNumber(totalTransfertAmount) }}</span> XOF
          </span>
          <span class="text-paragraph" v-if="validationErrors.length > 0">
            <i class="flaticon-alert me-1 text-danger"></i>
            <span class="text-danger fw-bold">{{ validationErrors.length }}</span> erreur(s) à corriger
          </span>
        </div>

        <!-- Pagination -->
        <nav>
          <ul class="pagination mb-0">
            <li :class="['page-item', { disabled: currentPage === 1 }]">
              <a class="page-link" @click="changePage(currentPage - 1)">
                <i class="flaticon-chevron-1"></i>
              </a>
            </li>
            <li v-for="page in displayedPages" :key="page" :class="['page-item', { active: page === currentPage }]">
              <a class="page-link" @click="changePage(page)">{{ page }}</a>
            </li>
            <li :class="['page-item', { disabled: currentPage === totalPages }]">
              <a class="page-link" @click="changePage(currentPage + 1)">
                <i class="flaticon-chevron"></i>
              </a>
            </li>
          </ul>
        </nav>
      </div>

      <!-- Actions en bas -->
      <div class="d-flex flex-wrap gap-2 justify-content-end mt-3">
        <!-- Messages d'information sur l'état du transfert -->
        <div class="w-100 mb-2" v-if="!canTransfer && transferts.length > 0">
          <div class="alert alert-warning alert-sm py-2 px-3 mb-0">
            <i class="flaticon-alert me-1"></i>
            <span v-if="!selectedCountry" class="fw-bold">Veuillez sélectionner un pays</span>
            <!-- <span v-else-if="totalTransfertAmount > payoutBalance" class="fw-bold">Solde insuffisant pour effectuer ce transfert</span> -->
            <span v-else-if="validationErrors.length > 0" class="fw-bold">
              {{ validationErrors.length }} erreur(s) à corriger avant le transfert
            </span>
          </div>
        </div>

        <button
          class="btn btn-outline-secondary btn-sm"
          @click="refreshPage"
        >
          <i class="flaticon-refresh me-1"></i>
          Rafraîchir
        </button>
        <button
          class="btn btn-sm text-white"
          style="background-color: #EB9355;"
          :disabled="!canTransfer || isLoading"
          @click="sendMoney"
          :title="getTransferButtonTitle()"
        >
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <i v-else class="flaticon-send me-1"></i>
          Transferer l'argent
        </button>
      </div>
    </div>
  </div>

  <!-- Modal d'importation des contacts -->
  <div class="modal fade" id="importModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Importation des contacts client</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeImportModal"></button>
        </div>
        <div class="modal-body">
          <div class="row g-2 mb-3">
            <div class="col-md-6" v-if="isSuperAdmin">
              <label class="fw-semibold">Pays</label>
              <select
                class="form-select form-select-sm shadow-none"
                @change="handleCountryChangeContact"
                v-model="selectedCountryContact"
              >
                <option value="">Sélectionnez...</option>
                <option value="SN">Sénégal</option>
                <option value="CI">Côte d'Ivoire</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="fw-semibold">Rechercher</label>
              <input
                type="text"
                class="form-control form-control-sm shadow-none"
                v-model="searchContact"
                @input="filterContacts"
                placeholder="Rechercher un contact..."
              />
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th class="ps-0">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      @change="selectAllContacts"
                      :checked="allSelected"
                    />
                  </th>
                  <th>Nom complet</th>
                  <th>Numéro de téléphone</th>
                  <th>Opérateur</th>
                  <th class="text-end">Montant</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredContacts.length === 0">
                  <td colspan="5" class="text-center text-muted py-30">
                    Aucun contact trouvé
                  </td>
                </tr>
                <tr v-for="contact in filteredContacts" :key="contact.code">
                  <td class="ps-0">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :value="contact.code"
                      v-model="selectedContacts"
                      @change="onContactSelect(contact)"
                    />
                  </td>
                  <td>{{ contact.first_name }} {{ contact.last_name }}</td>
                  <td>{{ contact.contact }}</td>
                  <td>
                    <select
                      class="form-select form-select-sm shadow-none"
                      :id="contact.code + ':operator'"
                      @change="onOperatorChange($event, contact)"
                    >
                      <option value="">Sélectionnez...</option>
                      <option v-for="op in availableOperators" :key="op.code" :value="op.mnoName">
                        {{ op.mnoName }}
                      </option>
                    </select>
                  </td>
                  <td class="text-end">
                    <input
                      type="number"
                      class="form-control form-control-sm shadow-none text-end"
                      :id="contact.code + ':amount'"
                      min="1"
                      @input="onAmountChange($event, contact)"
                      placeholder="Montant"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-outline-secondary" data-bs-dismiss="modal">
            Fermer
          </button>
          <button
            class="btn text-white"
            style="background-color: #EB9355;"
            :disabled="selectedContacts.length === 0 || isImporting"
            @click="importSelectedContacts"
          >
            <span v-if="isImporting" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="flaticon-import me-1"></i>
            Importer ({{ selectedContacts.length }})
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de notification des erreurs -->
  <div class="modal fade" id="notificationModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Rapport de validation</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <!-- Résumé des erreurs -->
          <div class="row g-3 mb-3">
            <div class="col-md-6 col-lg-4" v-for="(stat, key) in validationStats" :key="key">
              <div class="p-3 rounded" :class="stat.count === 0 ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'">
                <div class="d-flex justify-content-between align-items-center">
                  <span>{{ stat.label }}</span>
                  <span class="fw-bold" :class="stat.count === 0 ? 'text-success' : 'text-danger'">
                    {{ stat.count }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Tableau des erreurs -->
          <div class="table-responsive" v-if="validationErrors.length > 0">
            <table class="table table-hover align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th>Nom complet</th>
                  <th>Téléphone</th>
                  <th>Opérateur</th>
                  <th class="text-end">Montant</th>
                  <th>Erreurs</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="error in validationErrors" :key="error.code">
                  <td>{{ error.clientId }}</td>
                  <td>
                    <input
                      type="text"
                      class="form-control form-control-sm shadow-none"
                      :value="error.mobile"
                      :id="error.code + ':mobile'"
                      @input="onErrorFieldChange($event, error, 'mobile')"
                    />
                  </td>
                  <td>
                    <select
                      class="form-select form-select-sm shadow-none"
                      :id="error.code + ':operator'"
                      @change="onErrorOperatorChange($event, error)"
                    >
                      <option value="">Sélectionnez...</option>
                      <option v-for="op in availableOperators" :key="op.code" :value="op.mnoName" :selected="op.mnoName === error.operator">
                        {{ op.mnoName }}
                      </option>
                    </select>
                  </td>
                  <td class="text-end">
                    <input
                      type="number"
                      class="form-control form-control-sm shadow-none text-end"
                      :value="error.amount"
                      :id="error.code + ':amount'"
                      min="1"
                      @input="onErrorFieldChange($event, error, 'amount')"
                    />
                  </td>
                  <td>
                    <span v-for="err in error.errors" :key="err" class="badge bg-danger me-1">
                      {{ err }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer" v-if="validationErrors.length > 0">
          <button
            class="btn text-white"
            style="background-color: #EB9355;"
            @click="applyCorrections"
          >
            <i class="flaticon-check me-1"></i>
            Appliquer les corrections
          </button>
        </div>
      </div>
    </div>
  </div>

  <div id="snackbar"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, reactive, inject, computed } from "vue";
import axios from 'axios';
import router from '../../../router/index';
import { read, utils } from 'xlsx';
import { Modal } from 'bootstrap';
import { current_permissions } from '../../../utils/permission';
import { showToast, checkSessionValidate, strUUID } from '../../../utils/function';
import { Business, Contact, Operator } from "@/utils/Model";

const backApi = inject('back');

const token = localStorage.getItem('login_token');
const organizationCode = localStorage.getItem('organization_code');
const role = localStorage.getItem('role');
const isSuperAdmin = computed(() => role === 'SUPERADMIN');

// Infos nécessaires à la vérification du solde (comme sur la page transactions)
const usercode = localStorage.getItem('code');
const account_size = parseFloat(localStorage.getItem('account_size') + '');
const personnal_account_status = localStorage.getItem('personnal_account_status');

const loader = ref(true);
const isLoading = ref(false);
const isImporting = ref(false);

const currentPage = ref(1);
const itemsPerPage = ref(10);

const business = ref<Business[]>([]);
const subBusiness = ref<Business[]>([]);
const businessTemp = ref<Business[]>([]);
const operators = ref<Operator[]>([]);
const operatorsTemp = ref<Operator[]>([]);
const contacts = ref<Contact[]>([]);
const filteredContacts = ref<Contact[]>([]);
const transferts = ref<any[]>([]);

// Solde
const payoutBalance = ref(0);
let balance = 0;

// Filtres
const selectedBusiness = ref('');
const selectedSubBusiness = ref('');
const selectedCountry = ref('');
const selectedCountryContact = ref('');
const transferDate = ref('');
const searchContact = ref('');
const showSubBusiness = ref(false);

// Upload de fichier
const fileInput = ref<HTMLInputElement | null>(null);
const fileName = ref('');
const isDragging = ref(false);

const selectedContacts = ref<string[]>([]);
const allSelected = ref(false);
const contactSelections = reactive<Record<string, { operator: string; amount: number }>>({});
const contactData = reactive<Record<string, Contact>>({});

// Validation
const validationErrors = ref<any[]>([]);
const validationStats = reactive({
  amount: { count: 0, label: 'Erreurs de montant' },
  telSize: { count: 0, label: 'Numéros invalides' },
  telFormat: { count: 0, label: 'Format de téléphone' },
  telOperator: { count: 0, label: 'Opérateur inconnu' },
  duplicateTel: { count: 0, label: 'Doublons' },
});

// États UI
const errors = ref('');
const errorId = ref('');
const success = ref('');
const totalTransfertAmount = ref(0);

const EXPECTED_HEADERS = ['NOM', 'PRENOM', 'PHONE_NUMBER', 'AMOUNT', 'PAYMENT_METHOD'];

const PHONE_LENGTH_BY_COUNTRY: Record<string, number> = {
  CI: 10,
  SN: 9,
};

const COUNTRY_DIAL_CODES: Record<string, string> = {
  CI: '225',
  SN: '221',
};

function getPhoneLength(country: string): number {
  return PHONE_LENGTH_BY_COUNTRY[country] || 10;
}

function normalizePhoneNumber(rawMobile: any, country: string): string {
  let mobile = String(rawMobile ?? '').replace(/\s|-/g, '').trim();

  const hasPlus = mobile.startsWith('+');
  mobile = mobile.replace(/\D/g, '');

  if (!hasPlus && mobile.startsWith('00')) {
    mobile = mobile.substring(2);
  }

  const dialCode = COUNTRY_DIAL_CODES[country];
  const expectedLength = getPhoneLength(country);

  if (dialCode && mobile.startsWith(dialCode)) {
    const withoutCode = mobile.substring(dialCode.length);
    if (withoutCode.length === expectedLength) {
      mobile = withoutCode;
    }
  }

  return mobile;
}

// Computed
const totalBeneficiaries = computed(() => transferts.value.length);
const totalPages = computed(() => Math.ceil(transferts.value.length / itemsPerPage.value) || 1);

const displayedPages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    if (current <= 3) {
      pages.push(1, 2, 3, '...', total);
    } else if (current >= total - 2) {
      pages.push(1, '...', total - 2, total - 1, total);
    } else {
      pages.push(1, '...', current - 1, current, current + 1, '...', total);
    }
  }
  return pages;
});

const paginatedTransferts = computed(() => {
  const list = transferts.value || [];
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = Math.min(start + itemsPerPage.value, list.length);
  return list.slice(start, end);
});

// Le transfert est bloqué si : pas de bénéficiaires, pas de pays, erreurs de validation,
// chargement en cours, OU montant total > solde disponible.
const canTransfer = computed(() => {
  const hasBeneficiaries = transferts.value.length > 0;
  const hasCountry = selectedCountry.value !== '' && selectedCountry.value !== null;
  const noErrors = validationErrors.value.length === 0;
  const notLoading = !isLoading.value;
  // const hasSufficientBalance = totalTransfertAmount.value > 0 && totalTransfertAmount.value <= payoutBalance.value;

  return hasBeneficiaries && hasCountry && noErrors && notLoading;
});

const availableOperators = computed(() => operators.value);

// Méthodes
function formatNumber(value: number): string {
  return new Intl.NumberFormat('fr-FR').format(value || 0);
}

function showToastMessage(message: string, type: 'success' | 'error' | 'info' = 'success') {
  showToast(message, type);
}

function refreshPage() {
  router.go();
}

function changePage(page: number) {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
}

function getTransferButtonTitle(): string {
  if (transferts.value.length === 0) return 'Aucun bénéficiaire à transférer';
  if (!selectedCountry.value) return 'Veuillez sélectionner un pays';
  if (validationErrors.value.length > 0) return 'Corrigez les erreurs avant de transférer';
  if (isLoading.value) return 'Transfert en cours...';
  return 'Transférer l\'argent aux bénéficiaires';
}

//  SOLDE 

/**
 * Récupère le solde du compte "personnel" (payout perso), utilisé quand
 * le compte a un account_size > 0 et est ACTIVE — même logique que la page Transactions.
 */
function getTransfertBalancePerso() {
  return axios.post(`${backApi}/secure/accounts/balance/main`, {
    user: usercode,
  }, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then((response) => {
      payoutBalance.value = response.data.balance;
      balance = response.data.balance;
    })
    .catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          // router.push('/')
        }
      }
    });
}

/**
 * Récupère le solde de l'organisation (compte payout classique).
 */
function getBalancePayout() {
  return axios.post(`${backApi}/secure/accounts/balance/main`, {
    organization: organizationCode,
  }, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then((response) => {
      payoutBalance.value = response.data.balance;
      balance = response.data.balance;
    })
    .catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          router.push('/');
        }
      }
    });
}

function loadBalance() {
  if (account_size > 0 && personnal_account_status === "ACTIVE") {
    return getTransfertBalancePerso();
  }
  return getBalancePayout();
}

//  UPLOAD DE FICHIER 

function triggerFileUpload() {
  if (!selectedCountry.value) {
    showToastMessage('Veuillez d\'abord sélectionner un pays', 'error');
    return;
  }
  if (fileInput.value) {
    fileInput.value.click();
  }
}

function handleDrop(event: DragEvent) {
  isDragging.value = false;
  if (!selectedCountry.value) {
    showToastMessage('Veuillez d\'abord sélectionner un pays', 'error');
    return;
  }
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    const file = files[0];
    processFile(file);
  }
}

function handleFileUpload(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) {
    processFile(file);
  }
  input.value = '';
}

function processFile(file: File) {
  // Vérifier le type de fichier
  const validTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv'
  ];

  if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
    setFormatError(
      'Format de fichier non supporté',
      `Le fichier "${file.name}" n'est pas un fichier Excel/CSV valide. Utilisez un fichier au format .xlsx, .xls ou .csv, idéalement le modèle fourni via le bouton "Télécharger modèle".`
    );
    return;
  }

  // Vérifier la taille (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    setFormatError(
      'Fichier trop volumineux',
      'Le fichier dépasse la taille maximale autorisée de 5 Mo. Réduisez le nombre de lignes ou scindez le fichier en plusieurs imports.'
    );
    return;
  }

  fileName.value = file.name;
  showToastMessage(`Lecture du fichier: ${file.name}`, 'info');

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = utils.sheet_to_json(worksheet, { header: 1 });

      processExcelData(jsonData);

    } catch (error) {
      console.error('Erreur lors de la lecture du fichier:', error);
      setFormatError(
        'Fichier illisible',
        'Le fichier n\'a pas pu être lu. Assurez-vous qu\'il n\'est pas corrompu et qu\'il correspond bien au modèle fourni.'
      );
    }
  };
  reader.readAsArrayBuffer(file);
}

function setFormatError(title: string, detail: string) {
  errorId.value = title;
  errors.value = detail;
  showToastMessage(title, 'error');
}

function processExcelData(jsonData: any[]) {
  // Vérifier que le fichier contient des données
  if (!jsonData || jsonData.length < 2) {
    setFormatError(
      'Fichier vide',
      'Le fichier importé ne contient aucune ligne de bénéficiaire. Téléchargez le modèle et remplissez-le avant de l\'importer.'
    );
    return;
  }

  const headers = (jsonData[0] || []).map((h: any) => (h ?? '').toString().trim().toUpperCase());
  const missing = EXPECTED_HEADERS.filter((h, i) => headers[i] !== h);

  if (missing.length > 0) {
    setFormatError(
      'Format de fichier invalide',
      `Les colonnes du fichier ne correspondent pas au modèle attendu.\n` +
      `Colonnes attendues (dans cet ordre) : ${EXPECTED_HEADERS.join(', ')}.\n` +
      `Colonnes trouvées : ${headers.filter(Boolean).join(', ') || 'aucune'}.\n` +
      `Veuillez télécharger le modèle via "Télécharger modèle" et l'utiliser sans modifier l'ordre ou le nom des colonnes.`
    );
    return;
  }

  const phoneLength = getPhoneLength(selectedCountry.value);

  // Traiter les lignes
  const importedData: any[] = [];
  const seenMobiles = new Set<string>();
  let totalAmount = 0;
  let skippedIncomplete = 0;

  for (let i = 1; i < jsonData.length; i++) {
    const row = jsonData[i];
    if (!row || row.every((cell: any) => cell === undefined || cell === '')) continue; // ligne vide

    if (!row[0] || row[2] === undefined || row[2] === '' || row[3] === undefined || row[3] === '') {
      skippedIncomplete++;
      continue;
    }

    //  Nettoyage + retrait de l'indicatif pays éventuel
    const mobile = normalizePhoneNumber(row[2], selectedCountry.value);
    const amount = parseFloat(row[3]);

    const beneficiary = {
      clientId: `${row[0]} ${row[1] || ''}`.trim(),
      mobile,
      amount: isNaN(amount) ? 0 : amount,
      operator: (row[4] || '').toString().trim(),
      countryCode: selectedCountry.value,
      code: strUUID(),
      errors: [] as string[]
    };

    if (isNaN(beneficiary.amount) || beneficiary.amount <= 0) {
      beneficiary.errors.push('Montant invalide (doit être supérieur à 0)');
    }

    if (!/^\d+$/.test(beneficiary.mobile)) {
      beneficiary.errors.push('Format de téléphone invalide (chiffres uniquement)');
    } else if (beneficiary.mobile.length !== phoneLength) {
      beneficiary.errors.push(`Le numéro doit contenir ${phoneLength} chiffres (${selectedCountry.value === 'CI' ? 'Côte d\'Ivoire' : selectedCountry.value === 'SN' ? 'Sénégal' : 'pays sélectionné'})`);
    }

    if (seenMobiles.has(beneficiary.mobile)) {
      beneficiary.errors.push('Doublon détecté (numéro déjà présent dans le fichier)');
    } else {
      seenMobiles.add(beneficiary.mobile);
    }

    importedData.push(beneficiary);
    totalAmount += beneficiary.amount > 0 ? beneficiary.amount : 0;
  }

  if (importedData.length === 0) {
    setFormatError(
      'Aucune donnée valide',
      'Aucune ligne exploitable n\'a été trouvée dans le fichier. Vérifiez que le nom, le téléphone et le montant sont bien renseignés pour chaque bénéficiaire.'
    );
    return;
  }

  transferts.value = importedData;
  totalTransfertAmount.value = totalAmount;
  currentPage.value = 1;

  validateBeneficiaries(transferts.value);

  const skippedMsg = skippedIncomplete > 0 ? ` (${skippedIncomplete} ligne(s) incomplète(s) ignorée(s))` : '';

  if (validationErrors.value.length > 0) {
    showToastMessage(`${validationErrors.value.length} ligne(s) contiennent des erreurs${skippedMsg}`, 'error');
    const modalEl = document.getElementById('notificationModal');
    if (modalEl) {
      const modal = Modal.getOrCreateInstance(modalEl);
      modal.show();
    }
  } else if (totalAmount > payoutBalance.value) {
    showToastMessage(`Solde insuffisant : le montant total (${formatNumber(totalAmount)} XOF) dépasse votre solde disponible (${formatNumber(payoutBalance.value)} XOF)${skippedMsg}`, 'error');
  } else {
    showToastMessage(`${importedData.length} bénéficiaire(s) importé(s) avec succès${skippedMsg}`, 'success');
  }
}

function downloadTemplate() {
  window.open('/model_payout.xls', '_blank');
}


function getOperators() {
  return new Promise((resolve, reject) => {
    axios.get(`${backApi}/secure/operators/available/${process.env.VUE_APP_PAYOUT_OPTTYPE}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      operatorsTemp.value = response.data;
      operators.value = response.data;
      resolve(response);
    })
    .catch(error => {
      if (error.response?.status === 401) router.push('/');
      reject(error);
    });
  });
}


function getBusiness() {
  return new Promise((resolve, reject) => {
    const endpoint = isSuperAdmin.value
      ? `${backApi}/secure/business/extended/`
      : `${backApi}/secure/business/?organization=${organizationCode}`;

    axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    .then(response => {
      businessTemp.value = response.data;
      business.value = businessTemp.value.filter(item => item.parent === null);
      resolve(response);
    })
    .catch(error => {
      if (error.response?.status === 401) {
        router.push('/');
      }
      reject(error);
    });
  });
}


function getContacts() {
  const endpoint = isSuperAdmin.value
    ? `${backApi}/secure/contactbooks/extended/`
    : `${backApi}/secure/contactbook/?organization=${organizationCode}`;

  axios.get(endpoint, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
  .then(response => {
    contacts.value = response.data || [];
    filteredContacts.value = response.data || [];
  })
  .catch(error => {
    if (error.response?.status === 401) {
      router.push('/');
    }
  });
}

function filterContacts() {
  const query = searchContact.value.toLowerCase().trim();
  if (!query) {
    filteredContacts.value = contacts.value;
  } else {
    filteredContacts.value = contacts.value.filter(c =>
      (c.first_name?.toLowerCase().includes(query) || '') ||
      (c.last_name?.toLowerCase().includes(query) || '') ||
      (c.contact?.includes(query) || '')
    );
  }
}

//  VALIDATION 

function validateBeneficiaries(data: any[]) {
  validationErrors.value = [];

  const phoneLength = getPhoneLength(selectedCountry.value);

  const mobileCounts: Record<string, number> = {};
  data.forEach(item => {
    const key = normalizePhoneNumber(item.mobile, selectedCountry.value);
    mobileCounts[key] = (mobileCounts[key] || 0) + 1;
  });

  data.forEach(item => {
    const itemErrors: string[] = [];

    const phone = normalizePhoneNumber(item.mobile, selectedCountry.value);
    item.mobile = phone;

    if (item.amount === undefined || item.amount === null || isNaN(item.amount) || item.amount <= 0) {
      itemErrors.push('Montant invalide (doit être supérieur à 0)');
    }

    if (!/^\d+$/.test(phone)) {
      itemErrors.push('Format de téléphone invalide (chiffres uniquement)');
    } else if (phone.length !== phoneLength) {
      itemErrors.push(`Téléphone doit faire ${phoneLength} chiffres`);
    }

    if (item.operator) {
      const opExists = operators.value.some(o => o.mnoName === item.operator);
      if (!opExists) {
        itemErrors.push('Opérateur inconnu');
      }
    }

    if (mobileCounts[phone] > 1) {
      itemErrors.push('Doublon détecté');
    }

    if (itemErrors.length > 0) {
      item.errors = itemErrors;
      validationErrors.value.push(item);
    } else {
      item.errors = [];
    }
  });

  validationStats.amount.count = validationErrors.value.filter(e => e.errors.some((err: string) => err.startsWith('Montant invalide'))).length;
  validationStats.telSize.count = validationErrors.value.filter(e => e.errors.some((err: string) => err.includes('doit faire'))).length;
  validationStats.telFormat.count = validationErrors.value.filter(e => e.errors.some((err: string) => err.includes('Format de téléphone invalide'))).length;
  validationStats.telOperator.count = validationErrors.value.filter(e => e.errors.includes('Opérateur inconnu')).length;
  validationStats.duplicateTel.count = validationErrors.value.filter(e => e.errors.includes('Doublon détecté')).length;
}


function onErrorFieldChange(event: Event, item: any, field: string) {
  const rawValue = (event.target as HTMLInputElement).value;

  if (field === 'amount') {
    item.amount = parseFloat(rawValue) || 0;
  } else if (field === 'mobile') {
    item.mobile = normalizePhoneNumber(rawValue, item.countryCode || selectedCountry.value);
  } else {
    item[field] = rawValue;
  }

  const index = transferts.value.findIndex(t => t.code === item.code);
  if (index !== -1) {
    transferts.value[index][field] = item[field];
  }

  validateBeneficiaries(transferts.value);
  totalTransfertAmount.value = transferts.value.reduce((sum, t) => sum + (t.amount > 0 ? t.amount : 0), 0);
}

function onErrorOperatorChange(event: Event, item: any) {
  item.operator = (event.target as HTMLSelectElement).value;
  const index = transferts.value.findIndex(t => t.code === item.code);
  if (index !== -1) {
    transferts.value[index].operator = item.operator;
  }
  validateBeneficiaries(transferts.value);
}

function applyCorrections() {
  validateBeneficiaries(transferts.value);
  if (validationErrors.value.length === 0) {
    const modalEl = document.getElementById('notificationModal');
    if (modalEl) {
      const modal = Modal.getOrCreateInstance(modalEl);
      modal.hide();
    }
    showToastMessage('Toutes les erreurs ont été corrigées', 'success');
  } else {
    showToastMessage(`Il reste ${validationErrors.value.length} erreur(s) à corriger`, 'error');
  }
}


function openImportModal() {
  if (!selectedCountry.value) {
    showToastMessage('Veuillez d\'abord sélectionner un pays', 'error');
    return;
  }
  getContacts();
  selectedContacts.value = [];
  allSelected.value = false;
  const modalEl = document.getElementById('importModal');
  if (modalEl) {
    const modal = Modal.getOrCreateInstance(modalEl);
    modal.show();
  }
}

function closeImportModal() {
  selectedContacts.value = [];
  allSelected.value = false;
}

function onContactSelect(contact: Contact) {
  if (!contactData[contact.code]) {
    contactData[contact.code] = contact;
  }
}

function onOperatorChange(event: Event, contact: Contact) {
  const value = (event.target as HTMLSelectElement).value;
  if (!contactSelections[contact.code]) {
    contactSelections[contact.code] = { operator: '', amount: 0 };
  }
  contactSelections[contact.code].operator = value;
}

function onAmountChange(event: Event, contact: Contact) {
  const value = parseFloat((event.target as HTMLInputElement).value) || 0;
  if (!contactSelections[contact.code]) {
    contactSelections[contact.code] = { operator: '', amount: 0 };
  }
  contactSelections[contact.code].amount = value;
}

function selectAllContacts() {
  allSelected.value = !allSelected.value;
  if (allSelected.value) {
    selectedContacts.value = filteredContacts.value.map(c => c.code);
    filteredContacts.value.forEach(c => contactData[c.code] = c);
  } else {
    selectedContacts.value = [];
  }
}

function importSelectedContacts() {
  if (selectedContacts.value.length === 0) return;

  isImporting.value = true;
  const imported: any[] = [];
  const targetCountry = selectedCountryContact.value || selectedCountry.value;
  const phoneLength = getPhoneLength(targetCountry);
  let rejected = 0;

  selectedContacts.value.forEach(code => {
    const contact = contactData[code] || contacts.value.find(c => c.code === code);
    if (contact) {
      const selection = contactSelections[code] || { operator: '', amount: 0 };

      if (!selection.operator || !(selection.amount > 0)) {
        rejected++;
        return;
      }

      const mobile = normalizePhoneNumber(contact.contact, targetCountry);

      const alreadyExists = transferts.value.some(t => t.mobile === mobile) ||
        imported.some(t => t.mobile === mobile);

      if (alreadyExists) {
        rejected++;
        return;
      }

      imported.push({
        clientId: `${contact.first_name} ${contact.last_name}`.trim(),
        mobile,
        amount: selection.amount,
        operator: selection.operator,
        countryCode: targetCountry,
        code: strUUID(),
        errors: mobile.length === phoneLength ? [] : [`Le numéro doit contenir ${phoneLength} chiffres`]
      });
    }
  });

  if (imported.length > 0) {
    transferts.value = [...transferts.value, ...imported];

    validateBeneficiaries(transferts.value);
    totalTransfertAmount.value = transferts.value.reduce((sum, t) => sum + (t.amount > 0 ? t.amount : 0), 0);

    const rejectedMsg = rejected > 0 ? ` (${rejected} contact(s) ignoré(s) : doublon, montant ou opérateur manquant)` : '';

    if (validationErrors.value.length === 0) {
      const modalEl = document.getElementById('importModal');
      if (modalEl) {
        const modal = Modal.getOrCreateInstance(modalEl);
        modal.hide();
      }
      if (totalTransfertAmount.value > payoutBalance.value) {
        showToastMessage(`${imported.length} contacts importés, mais le solde est insuffisant pour ce montant${rejectedMsg}`, 'error');
      } else {
        showToastMessage(`${imported.length} contacts importés avec succès${rejectedMsg}`, 'success');
      }
    } else {
      showToastMessage(`${imported.length} contacts importés, ${validationErrors.value.length} à corriger${rejectedMsg}`, 'error');
      const modalEl = document.getElementById('notificationModal');
      if (modalEl) {
        const modal = Modal.getOrCreateInstance(modalEl);
        modal.show();
      }
    }
  } else {
    showToastMessage('Aucun contact valide sélectionné (opérateur, montant > 0, ou doublon détecté)', 'error');
  }

  isImporting.value = false;
  selectedContacts.value = [];
  allSelected.value = false;
}


function handleBusinessChange() {
  showSubBusiness.value = false;
  if (selectedBusiness.value) {
    const parent = businessTemp.value.find(b => b.code === selectedBusiness.value);
    if (parent?.validatePayoutBeforProcess) {
      showToastMessage('Cette catégorie nécessite une validation', 'info');
    }
    subBusiness.value = businessTemp.value.filter(b => b.parent?.code === selectedBusiness.value);
    showSubBusiness.value = subBusiness.value.length > 0;
  }
}

function handleSubBusinessChange() {
  if (selectedSubBusiness.value) {
    const item = businessTemp.value.find(b => b.code === selectedSubBusiness.value);
    if (item?.validatePayoutBeforProcess) {
      showToastMessage('Cette catégorie nécessite une validation', 'info');
    }
  }
}

function handleCountryChange() {
  if (selectedCountry.value) {
    operators.value = operatorsTemp.value.filter(o => o.countryCode === selectedCountry.value);
  } else {
    operators.value = operatorsTemp.value;
  }

  if (transferts.value.length > 0) {
    transferts.value.forEach(t => {
      t.mobile = normalizePhoneNumber(t.mobile, selectedCountry.value);
      t.countryCode = selectedCountry.value;
    });
    validateBeneficiaries(transferts.value);
    showToastMessage(`Numéros revalidés pour ${getPhoneLength(selectedCountry.value)} chiffres`, 'info');
  }

  if (fileInput.value) {
    fileInput.value.value = '';
  }
  fileName.value = '';
}

function handleCountryChangeContact() {
  if (selectedCountryContact.value) {
    operators.value = operatorsTemp.value.filter(o => o.countryCode === selectedCountryContact.value);
  } else {
    operators.value = operatorsTemp.value;
  }
}

function resetBeneficiaries() {
  transferts.value = [];
  totalTransfertAmount.value = 0;
  validationErrors.value = [];
  Object.keys(validationStats).forEach(key => validationStats[key as keyof typeof validationStats].count = 0);
}

function removeBeneficiary(index: number) {
  const realIndex = (currentPage.value - 1) * itemsPerPage.value + index;
  if (realIndex >= 0 && realIndex < transferts.value.length) {
    const removed = transferts.value[realIndex];
    transferts.value.splice(realIndex, 1);
    totalTransfertAmount.value -= removed.amount || 0;
    validateBeneficiaries(transferts.value);
    if (transferts.value.length === 0) {
      currentPage.value = 1;
    }
  }
}


/**
 * Envoie les transferts vers le backend.
 * Vérifications appliquées avant l'appel API (dans l'ordre) :
 *  1. Il doit y avoir au moins un bénéficiaire.
 *  2. Un pays doit être sélectionné.
 *  3. Aucune erreur de validation ne doit subsister sur la liste.
 *  4. Le montant total à transférer ne doit pas dépasser le solde disponible (payoutBalance).
 */
function sendMoney() {
  if (!canTransfer.value) {
    let message = 'Veuillez vérifier les éléments suivants:\n';
    if (transferts.value.length === 0) message += '- Aucun bénéficiaire\n';
    if (!selectedCountry.value) message += '- Pays non sélectionné\n';
    if (validationErrors.value.length > 0) message += '- Des erreurs doivent être corrigées\n';
    if (totalTransfertAmount.value > payoutBalance.value) {
      message += `- Solde insuffisant : montant total ${formatNumber(totalTransfertAmount.value)} XOF > solde disponible ${formatNumber(payoutBalance.value)} XOF\n`;
    }
    showToastMessage(message, 'error');
    return;
  }

  // Double vérification explicite du solde juste avant l'appel réseau
  // (protège contre un montant modifié après le calcul de canTransfer).
  if (totalTransfertAmount.value > payoutBalance.value) {
    errorId.value = 'Solde insuffisant';
    errors.value = `Le montant total à transférer (${formatNumber(totalTransfertAmount.value)} XOF) dépasse votre solde disponible (${formatNumber(payoutBalance.value)} XOF).`;
    showToastMessage(errors.value, 'error');
    return;
  }

  isLoading.value = true;
  const transactionData = transferts.value.map(t => ({
    clientId: t.clientId,
    mobile: t.mobile,
    amount: t.amount,
    operator: t.operator,
    countryCode: selectedCountry.value
  }));

  const payload = {
    payoutType: 2,
    operationType: parseInt(process.env.VUE_APP_PAYOUT_OPTTYPE || '1'),
    scheduledAt: transferDate.value || null,
    business: selectedBusiness.value || null,
    transactions: transactionData
  };

  axios.post(`${backApi}/secure/transactions/init`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
  .then(response => {
    isLoading.value = false;
    showToastMessage('Transfert effectué avec succès', 'success');
    setTimeout(() => refreshPage(), 2000);
  })
  .catch(error => {
    isLoading.value = false;
    if (error.response) {
      if (error.response.status === 401) {
        router.push('/');
      } else {
        errors.value = error.response.data.message || 'Erreur lors du transfert';
        errorId.value = error.response.data.errorId || '';
        showToastMessage(errors.value, 'error');
      }
    }
  });
}


onMounted(async () => {
  checkSessionValidate(1);
  loader.value = false;

  try {
    await Promise.all([
      getOperators(),
      getBusiness(),
      loadBalance()
    ]);
    loader.value = true;
  } catch (error) {
    console.error('Erreur lors du chargement:', error);
    loader.value = true;
  }
});
</script>

<style scoped>
.card-head {
  border-bottom: 1px solid #e9ecef;
}

.drop-zone {
  border: 2px dashed #dee2e6;
  transition: all 0.3s ease;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
}

.drop-zone.border-primary {
  border-color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.05);
}

.border-2 {
  border-width: 2px !important;
}

.border-dashed {
  border-style: dashed !important;
}

.table th {
  color: #6c757d;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.bg-success.bg-opacity-10 {
  background-color: rgba(25, 135, 84, 0.1) !important;
}

.bg-danger.bg-opacity-10 {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.form-select-sm, .form-control-sm {
  font-size: 0.875rem;
}

.btn-sm {
  font-size: 0.875rem;
}

.pagination-area {
  border-top: 1px solid #e9ecef;
  padding-top: 15px;
}

.text-paragraph {
  color: #6c757d;
}

.page-item {
  cursor: pointer;
}

.page-item.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.page-item.active .page-link {
  background-color: #EB9355;
  border-color: #EB9355;
  color: white;
}

.page-link {
  color: #333;
}

.page-link:hover {
  background-color: #f8f9fa;
  color: #EB9355;
}

.alert-sm {
  font-size: 0.875rem;
}

@media (max-width: 767px) {
  .table-responsive table {
    font-size: 13px;
  }
  .drop-zone {
    min-height: 100px;
    padding: 1rem !important;
  }
}
</style>